import { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRemoteFetchUserInfoForAllUsers } from "../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { useRemoteFetchVisitorBookingSchedules } from "../../hooks/Remote/Schedule/useRemoteFetchVisitorBookingSchedules";
import { VisitorBookingsTable } from "./components/visitor-bookings-table.component";
import { VisitorBookingsMode } from "../../hooks/Remote/Schedule/visitor-booking-schedules.api";
import { CustomColoredTabs } from "../../components/CustomColoredTabs/CustomColoredTabs.component";
import { CustomColoredTab } from "../../components/CustomColoredTabs/CustomColoredTab.component";
import { TabPanel } from "../TabPanel/TabPanel.component";
import { a11yProps } from "../TabPanel/TabPanel.partial";
import { VisitorConsentContentEditor } from "../VisitorConsent/visitor-consent-content-editor.component";
import { VisitorConsentContentProvider } from "../VisitorConsent/visitor-consent-content.context";
import { VisitorConsentDocumentsTable } from "../VisitorConsent/visitor-consent-documents-table.component";
import { VisitorConsentDocumentsProvider } from "../VisitorConsent/visitor-consent-documents.context";

const VisitorBookingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [tabState, setTabState] = useState(1);

  const {
    data: colleagues,
    isLoading: colleaguesLoading,
    isFetched: colleaguesFetched
  } = useRemoteFetchUserInfoForAllUsers();

  const fetchEnabled = colleaguesFetched && !colleaguesLoading;
  const mode: VisitorBookingsMode = tabState === 0 ? "archive" : "active";

  const {
    data: visitBookings,
    isLoading: visitBookingsLoading,
    refetch: refetchVisitBookings,
    isFetching: visitBookingsIsFetching
  } = useRemoteFetchVisitorBookingSchedules(mode, fetchEnabled && tabState < 2);

  return (
    <Box data-testid="visitor-bookings-admin-page" sx={{ p: 1 }}>
      <Stack spacing={2}>
        <CustomColoredTabs
          value={tabState}
          onChange={(_, newValue: number) => setTabState(newValue)}
          autoScrollable
        >
          <CustomColoredTab
            index={0}
            data-testid="visitor-bookings-tab-archive"
            label={t("_visitorBookings_tabArchive")}
            {...a11yProps(0)}
          />
          <CustomColoredTab
            index={1}
            data-testid="visitor-bookings-tab-bookings"
            label={t("_visitorBookings_tabBookings")}
            {...a11yProps(1)}
          />
          <CustomColoredTab
            index={2}
            data-testid="visitor-bookings-tab-docs"
            label={t("_visitorBookings_tabDocs")}
            {...a11yProps(2)}
          />
          <CustomColoredTab
            index={3}
            data-testid="visitor-bookings-tab-consent-page"
            label={t("_visitorBookings_tabConsentPage")}
            {...a11yProps(3)}
          />
        </CustomColoredTabs>

        <TabPanel value={tabState} index={0}>
          <VisitorBookingsTable
            mode={mode}
            rows={mode === "archive" ? visitBookings ?? [] : []}
            colleagues={colleagues ?? []}
            isLoading={visitBookingsLoading || visitBookingsIsFetching}
            onRefetch={() => refetchVisitBookings()}
          />
        </TabPanel>

        <TabPanel value={tabState} index={1}>
          <VisitorBookingsTable
            mode={mode}
            rows={mode === "active" ? visitBookings ?? [] : []}
            colleagues={colleagues ?? []}
            isLoading={visitBookingsLoading || visitBookingsIsFetching}
            onRefetch={() => refetchVisitBookings()}
          />
        </TabPanel>

        <TabPanel value={tabState} index={2}>
          <VisitorConsentDocumentsProvider>
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary">
                {t("_visitorBookings_docsIntro")}
              </Typography>

              <VisitorConsentDocumentsTable />
            </Stack>
          </VisitorConsentDocumentsProvider>
        </TabPanel>

        <TabPanel value={tabState} index={3}>
          <VisitorConsentContentProvider>
            <VisitorConsentContentEditor />
          </VisitorConsentContentProvider>
        </TabPanel>
      </Stack>
    </Box>
  );
};

export default VisitorBookingsPage;
